import React, { useState } from 'react';
import { useReactMediaRecorder } from "react-media-recorder";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import './App.css';

function App() {
  const { status, startRecording, stopRecording, mediaBlobUrl, error, clearBlobUrl } =
    useReactMediaRecorder({ 
      audio: true,
      askPermissionOnMount: false, // Solicitar permiso solo al grabar
      stopStreamsOnStop: true, // Detener la transmisión de media al parar
      onStop: (blobUrl, blob) => {
        console.log("Grabación detenida", blobUrl, blob);
      },
      onStart: () => {
        console.log("Grabación iniciada");
        setTranscription("");
      },
      onError: (err) => {
        console.error("Error de grabación:", err);
      }
    });

  const [transcription, setTranscription] = useState("");
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [browserSupport, setBrowserSupport] = useState(true);
  
  const { executeRecaptcha } = useGoogleReCaptcha();

  React.useEffect(() => {
    // Verificar soporte básico
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setBrowserSupport(false);
      setPermissionError("Tu navegador no soporta la grabación de audio.");
      return;
    }

    // Intentar listar dispositivos para ver si hay micrófonos disponibles
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const hasAudioInput = devices.some(device => device.kind === 'audioinput');
        if (!hasAudioInput) {
           setPermissionError("No se detectó ningún micrófono. Por favor conecta uno.");
        }
      })
      .catch(err => {
        console.error("Error al enumerar dispositivos:", err);
        setPermissionError("No se pudo acceder a la lista de dispositivos de audio.");
      });

  }, []);

  const handleDownload = async () => {
    if (mediaBlobUrl) {
      try {
        const response = await fetch(mediaBlobUrl);
        const blob = await response.blob();
        
        // Crear un enlace para descargar el archivo
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        // Nota: El navegador puede grabar en webm/ogg dependiendo del soporte.
        // Aquí lo nombramos .wav como solicitaste, pero ten en cuenta que el codec interno
        // será el que el navegador use por defecto (usualmente Opus en WebM para Chrome).
        a.download = "grabacion.wav"; 
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error("Error al descargar el audio:", error);
      }
    }
  };

  const handleTranscribe = async () => {
    if (!mediaBlobUrl) return;

    if (!executeRecaptcha) {
      console.log('Recaptcha has not been loaded');
      return;
    }

    setIsTranscribing(true);
    setTranscription("");
    
    try {
      // 0. Obtener token de reCAPTCHA v3
      const token = await executeRecaptcha('transcribe_audio');
      
      // 1. Obtener el archivo de audio del blob URL
      const audioResponse = await fetch(mediaBlobUrl);
      const audioBlob = await audioResponse.blob();
      
      // 2. Convertir Blob a Base64 (Promesa)
      const base64data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
      });

      // 3. Enviar la petición a la Cloud Function (formato Callable)
      // La Cloud Function espera un objeto { data: { ... } }
      const response = await fetch("https://transcribeaudio-g475okyxfq-uc.a.run.app", {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
          },
          body: JSON.stringify({
              data: {
                  audioBase64: base64data,
                  recaptchaToken: token // Nombre corregido para coincidir con backend
              }
          }),
      });

      if (!response.ok) {
        // Intentar leer el error del cuerpo si es posible
        const errorBody = await response.text();
        throw new Error(`Error en la API: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const result = await response.json();
      console.log("Respuesta completa:", result);
      
      // 4. Mostrar la transcripción
      // Firebase Callable devuelve { result: { ... } }
      // Asegurar acceso a la propiedad correcta: result.data.transcription o result.result.transcription
      const text = result.result?.transcription || result.data?.transcription || result.result?.text || "No se recibió texto";
      
      if (!text || text === "No se recibió texto") {
         console.warn("Estructura inesperada:", result);
      }
      
      setTranscription(text);
    } catch (error) {
       console.error("Error al transcribir:", error);
       setTranscription(`Error: ${error.message}`);
    } finally {
      setIsTranscribing(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Grabadora de Audio para Transcripción</h1>
        
        {permissionError && <div className="error-message p-3 bg-red-800 rounded">{permissionError}</div>}

        <div className="recorder-container">
          <p>Estado: <strong>{status}</strong></p>
          
          <div className="controls">
            {error && <p style={{ color: "red" }}>Error de la API: {error}</p>}
            {permissionError && <p style={{ color: "red" }}>Error de permisos/hardware: {permissionError}</p>}
            <button 
              className="btn btn-record" 
              onClick={() => {
                clearBlobUrl();
                startRecording();
              }}
              disabled={status === "recording"}
            >
              Iniciar Grabación
            </button>
            <button 
              className="btn btn-stop" 
              onClick={stopRecording}
              disabled={status !== "recording"}
            >
              Detener Grabación
            </button>
          </div>

          {mediaBlobUrl && (
            <div className="audio-player">
              <h3>Vista previa:</h3>
              <audio src={mediaBlobUrl} controls />
              
              <div className="action-buttons">
                <button className="btn btn-download" onClick={handleDownload} disabled={isTranscribing}>
                  Descargar .wav
                </button>
                <button className="btn btn-transcribe" onClick={handleTranscribe} disabled={isTranscribing}>
                  {isTranscribing ? "Transcribiendo..." : "Transcribir Audio"}
                </button>
              </div>
            </div>
          )}

          {transcription && (
            <div className="transcription-result">
              <h3>Resultado:</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>{transcription}</p>
            </div>
          )}
        </div>
      </header>
      <footer className="footer">
        <p>© {new Date().getFullYear()} javimoralesg. All rights reserved.</p>
        <p className="recaptcha-policy">
          Protected by reCAPTCHA.  
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer"> Privacy</a> and 
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer"> Terms</a>.
        </p>
      </footer>
    </div>
  );
}

export default App;
